import * as React from 'react'
import type { CameraType } from '../../../shared/domain/flows/types.no-deps'
import type { CommonDeps } from '../base/CommonDeps'
import { renderCatch } from '../error/renderCatch'
import { reduceRectToTarget } from '../photo/reduceRectToTarget'
import type { DeviceIndexLookup } from './DeviceIndexLookup'

interface RenderProps {
    videoElement: React.ReactElement<unknown>
    devices: MediaDeviceInfo[]
    recording: boolean
    takePhoto: (targetDimension: number) => Promise<Blob>
    toggleCamera: () => void
    recordVideo: () => Promise<Blob>
    stopRecording: () => void
}

interface Props {
    deps: CommonDeps
    onFailure: () => void
    children: (props: RenderProps) => React.ReactElement<unknown>
    browserCameraDeviceIndexLookup: DeviceIndexLookup
    videoTrackConstraints?: MediaTrackConstraints
    mode?: CameraType
}

interface State {
    currentDeviceIndex: number
    devices: MediaDeviceInfo[]
    recording: boolean
}

export class BrowserPhotoCapture extends React.Component<Props, State> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    private stream: MediaStream
    private videoElement: HTMLVideoElement
    private media_recorder: MediaRecorder = null
    private blobs_recorded: Blob[] = []

    public state: State = { currentDeviceIndex: null, devices: [], recording: false }

    public async componentDidMount() {
        try {
            // Force request of permissions ahead of enumerating devices, to ensure all are available
            const stream = await this.getStream()
            this.stopStream(stream)

            // Get a list of all available devices
            // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices
            const allDevices = await window.navigator.mediaDevices.enumerateDevices()

            // We only need video devices
            const devices = allDevices.filter((d) => d.kind === 'videoinput')

            // Get the previous index used
            const previousIndex = await this.props.browserCameraDeviceIndexLookup.get()

            // Fallback to the first device if none stored
            const currentDeviceIndex = previousIndex ?? 0

            this.setState({ devices, currentDeviceIndex })
        } catch (error) {
            this.props.deps.reportError(error)
            this.props.onFailure()
        }
    }

    public async componentDidUpdate(_: Props, prevState: State) {
        // Only update video stream if device changed
        if (prevState.currentDeviceIndex === this.state.currentDeviceIndex) return

        try {
            const { currentDeviceIndex, devices } = this.state

            if (this.state.recording) this.setState({ recording: false })
            this.stopStream(this.stream)

            this.stream = await this.getStream(devices[currentDeviceIndex].deviceId)

            // Feed the stream to the video element
            try {
                this.videoElement.srcObject = this.stream
            } catch (error) {
                this.videoElement.src = window.URL.createObjectURL(this.stream as any)
            }

            this.videoElement.onplay = () => this.setState({ recording: true })
        } catch (error) {
            this.props.deps.reportError(error)
            this.props.onFailure()
        }
    }

    public componentWillUnmount() {
        this.stopStream(this.stream)
    }

    private stopStream(stream: MediaStream) {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop())
        }
    }

    private async getStream(deviceId?: string) {
        // Get the stream for the selected device
        // https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/getUserMedia
        return window.navigator.mediaDevices.getUserMedia({
            audio: true,
            video: {
                // https://developer.mozilla.org/en-US/docs/Web/API/Media_Streams_API/Constraints
                deviceId,
                ...this.props.videoTrackConstraints,
            },
        })
    }

    private takePhoto = async (targetDimension: number) => {
        if (!this.stream) return null

        const { width, height } = reduceRectToTarget({
            target: targetDimension,
            width: this.videoElement.videoWidth,
            height: this.videoElement.videoHeight,
        })

        const canvas = document.createElement('canvas')

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(this.videoElement, 0, 0, width, height)

        return new Promise<Blob>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 1.0))
    }

    private recordVideo = async () => {
        return new Promise<Blob>((resolve) => {
            if (!this.stream) return resolve(null)

            this.media_recorder = new MediaRecorder(this.stream, { mimeType: 'video/webm' })

            // event : new recorded video blob available
            this.media_recorder.addEventListener('dataavailable', (e: any) => {
                this.blobs_recorded.push(e.data)
            })

            // event : recording stopped & all blobs sent
            this.media_recorder.addEventListener('stop', () => {
                resolve(new Blob(this.blobs_recorded, { type: 'video/webm' }))
            })

            // start recording with each recorded blob having 1 second video
            this.media_recorder.start(1000)
        })
    }

    private stopRecording = () => {
        this.media_recorder.stop()
    }

    private toggleCamera = async () => {
        const { devices } = this.state

        // Calculate the next device, looping around to the begining if at end
        const currentDeviceIndex = (this.state.currentDeviceIndex + 1) % devices.length

        // Update the camera being used
        this.setState({ currentDeviceIndex })

        // Save the index to start with next time
        await this.props.browserCameraDeviceIndexLookup.set(currentDeviceIndex)
    }

    public tryRender = () => {
        return this.props.children({
            videoElement: (
                <div className="AppCore__photo__BrowserPhotoCapture">
                    <video
                        className="AppCore__photo__BrowserPhotoCapture__video"
                        autoPlay
                        muted
                        ref={(ref) => (this.videoElement = ref)}
                    />
                </div>
            ),
            devices: this.state.devices,
            recording: this.state.recording,
            takePhoto: this.takePhoto,
            toggleCamera: this.toggleCamera,
            recordVideo: this.recordVideo,
            stopRecording: this.stopRecording,
        })
    }
}
